import React, {
  memo, useCallback, useEffect, useId, useRef, useState,
} from 'react'

import {
  BrainAIModel,
  Button,
  Check,
  ChevronDown,
  ChevronUp,
  ClosingDropdown,
  Icon,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'

import { MODELS } from './models'

const modelIcon = <Icon svgComponent={BrainAIModel} size={iconSize} color="dark" notSpaced />
const chevronUpIcon = <Icon svgComponent={ChevronUp} size={iconSize} color="dark" notSpaced />
const chevronDownIcon = <Icon svgComponent={ChevronDown} size={iconSize} color="dark" notSpaced />
// Every label shares one grid cell so the row trigger keeps the longest label's width
// whichever model is selected, and the composer row does not reflow on a switch.
const renderRowLabel = selectedId => (
  <span className="model-selector__label">
    {MODELS.map(({ id, label }) => (
      <span
        key={id}
        className={classnames('model-selector__label-text', id !== selectedId && 'model-selector__label-text--hidden')}
      >
        {label}
      </span>
    ))}
  </span>
)
const checkIcon = (
  <span className="model-selector__check">
    <Icon svgComponent={Check} size={iconSize} color="dark" notSpaced />
  </span>
)

// isStacked is the settings popover copy: a full-width row whose list expands inline,
// since a dropdown nested in the popover would stack a second overlay on top of it.
function ModelSelector({ value, onChange, isStacked = false }) {
  const listId = useId()
  const stackedRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)

  // The stacked list has no dropdown to dismiss it, so a press anywhere else - the rest
  // of the settings popover included - closes it.
  useEffect(() => {
    if (!isStacked || !isOpen) return undefined

    const handlePointerDown = (event) => {
      if (!stackedRef.current?.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)

    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isStacked, isOpen])

  const handleSelect = useCallback((id) => {
    onChange(id)
    if (isStacked) {
      setIsOpen(false)
    } else {
      setTriggerCloseDropdown(true)
    }
  }, [isStacked, onChange])

  const handleToggle = useCallback(() => {
    setIsOpen(current => !current)
  }, [])

  const selected = MODELS.find(model => model.id === value) || MODELS[0]

  const menu = (
    <ul
      id={listId}
      className={classnames('model-selector__menu', isStacked && 'model-selector__menu--stacked')}
      role="listbox"
      aria-label="AI model"
    >
      {MODELS.map(({ id, label }) => {
        const isSelected = id === selected.id
        return (
          <li
            key={id}
            className="model-selector__row"
            role="option"
            aria-selected={isSelected}
          >
            <Button
              type="ghost"
              size="small"
              variant="with-icon"
              actionType="button"
              customClass={classnames(
                'model-selector__option cgt-body-base-semibold',
                isStacked && 'model-selector__option--stacked',
              )}
              trailIcon={isSelected ? checkIcon : null}
              onClick={() => handleSelect(id)}
            >
              {label}
            </Button>
          </li>
        )
      })}
    </ul>
  )

  const trigger = (
    <Button
      type="secondary"
      size="small"
      variant="with-icon"
      actionType="button"
      icon={modelIcon}
      trailIcon={isOpen ? chevronUpIcon : chevronDownIcon}
      customClass={classnames(
        'model-selector__trigger cgt-button-text-base-semibold',
        isOpen && 'model-selector__trigger--open',
        isStacked && 'model-selector__trigger--stacked',
      )}
      aria-label={`AI model (${selected.label})`}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={isStacked ? listId : undefined}
      onClick={isStacked ? handleToggle : undefined}
    >
      {isStacked ? selected.label : renderRowLabel(selected.id)}
    </Button>
  )

  if (isStacked) {
    return (
      <div
        ref={stackedRef}
        className={classnames('model-selector model-selector--stacked', isOpen && 'model-selector--open')}
      >
        {trigger}
        {isOpen && menu}
      </div>
    )
  }

  return (
    <ClosingDropdown
      content={menu}
      isSimpleContent
      size="medium"
      placement="top-start"
      contentClassName="model-selector__popover"
      className="model-selector"
      triggerCloseDropdown={triggerCloseDropdown}
      setTriggerCloseDropdown={setTriggerCloseDropdown}
      onOpenChange={setIsOpen}
    >
      {trigger}
    </ClosingDropdown>
  )
}

ModelSelector.propTypes = {
  value: string.isRequired,
  onChange: func.isRequired,
  isStacked: bool,
}

export default memo(ModelSelector)
